// 가입 폼 상태 — 컴포넌트가 아닌 값·함수는 여기에 둔다(signup-form.tsx 의 Fast Refresh 가 깨지지 않게).

import { parseSignupInput, type SignupField } from "@buildpy/core/modules/onboarding/rules";
import type { TurnstileFailure } from "@/lib/turnstile";

export const SIGNUP_FAILED_MESSAGE = "가입하지 못했습니다. 잠시 후 다시 시도하세요.";

// 칸 오류면 field 가 있다 — 그 칸 아래에 띄우고 포커스한다 · 없으면 폼 아래 한 줄.
export interface SignupFormError {
  message: string;
  field: SignupField | null;
  // 오류 줄에 「다시 시도」 버튼을 붙인다 — Turnstile 을 못 불러왔거나 확인이 실패했을 때만.
  retry?: "turnstile";
}

// 서버 검사와 같은 한 벌로 미리 본다 — 서버 검사는 그대로 다시 한다.
export const precheckSignup = (input: { name: string; email: string }): SignupFormError | null => {
  const parsed = parseSignupInput(input);
  return parsed.ok ? null : { message: parsed.error, field: parsed.field };
};

export const TURNSTILE_REQUIRED_MESSAGE = "자동 가입 방지 확인을 마친 뒤 보내 주세요";

export const TURNSTILE_LOAD_FAILED_ERROR: SignupFormError = {
  message: "자동 가입 방지 확인을 불러오지 못했습니다. 새로고침하거나 광고 차단을 끄고 다시 시도해 주세요.",
  field: null,
  retry: "turnstile",
};

export const TURNSTILE_CHALLENGE_FAILED_ERROR: SignupFormError = {
  message: "자동 가입 방지 확인에 실패했습니다. 다시 시도를 눌러 확인을 다시 받아 주세요.",
  field: null,
  retry: "turnstile",
};

export const turnstileFailureError = (failure: TurnstileFailure): SignupFormError =>
  failure === "load" ? TURNSTILE_LOAD_FAILED_ERROR : TURNSTILE_CHALLENGE_FAILED_ERROR;

// 사이트 키가 있을 때만 토큰을 싣는다(키가 없으면 지금 모양 그대로) · 키가 있는데 토큰이 없으면 막는다 · 위젯이 실패했으면(불러오기·확인) 그 안내로 막는다.
export const withTurnstile = <T extends object>(
  input: T,
  siteKey: string | null,
  token: string | null,
  failure: TurnstileFailure | null = null,
): { ok: true; input: T & { turnstileToken?: string } } | { ok: false; error: SignupFormError } => {
  if (!siteKey) return { ok: true, input };
  if (failure) return { ok: false, error: turnstileFailureError(failure) };
  if (!token) return { ok: false, error: { message: TURNSTILE_REQUIRED_MESSAGE, field: null } };
  return { ok: true, input: { ...input, turnstileToken: token } };
};

export const signupErrorOf = (result: { message: string; field?: SignupField }): SignupFormError => ({ message: result.message, field: result.field ?? null });

// 오류 칸으로 포커스를 옮긴다 — 칸 오류가 아니면 그대로 둔다.
export const focusSignupError = (form: Pick<HTMLFormElement, "elements"> | null, error: SignupFormError | null): void => {
  if (!error?.field) return;
  const target = form?.elements.namedItem(error.field);
  if (target && "focus" in target && typeof target.focus === "function") target.focus();
};

// 사용자가 오류난 칸을 고치기 시작하면 그 칸 오류만 지운다.
export const clearFieldError = (error: SignupFormError | null, field: SignupField): SignupFormError | null => (error?.field === field ? null : error);

type ErrorSetter = (next: SignupFormError | null | ((current: SignupFormError | null) => SignupFormError | null)) => void;

// 다음 프레임에 run 을 부르고 취소 함수를 돌려준다.
export type NextFrame = (run: () => void) => () => void;

const animationFrame: NextFrame = (run) => {
  const id = requestAnimationFrame(run);
  return () => cancelAnimationFrame(id);
};

const noop = (): void => undefined;

// 오류를 비우고 다음 프레임에 다시 넣는다 — 같은 오류로 다시 보내도 live 영역이 바뀜을 알아채 다시 읽는다 · 돌려준 함수로 취소한다.
export const reannounceError = (setError: ErrorSetter, error: SignupFormError | null, nextFrame: NextFrame = animationFrame): (() => void) => {
  setError(null);
  return error ? nextFrame(() => setError(error)) : noop;
};

// 예약된 재안내를 하나만 들고 있다 — 다시 보내기·언마운트에서 취소하고 · 칸 입력은 예약된 오류가 그 칸 것(또는 칸 없는 폼 오류)일 때만 취소해 고친 뒤 옛 오류가 되살아나지 않게.
export const createErrorAnnouncer = (setError: ErrorSetter, nextFrame: NextFrame = animationFrame) => {
  let cancel = noop;
  let scheduled: SignupFormError | null = null;
  const stop = () => {
    cancel();
    cancel = noop;
    scheduled = null;
  };
  return {
    announce: (error: SignupFormError | null) => {
      stop();
      scheduled = error;
      cancel = reannounceError(setError, error, (run) =>
        nextFrame(() => {
          scheduled = null;
          run();
        }),
      );
    },
    fieldChanged: (field: SignupField) => {
      // 다른 칸 오류의 재안내는 그대로 둔다 — 그 칸은 아직 안 고쳤다.
      if (!scheduled?.field || scheduled.field === field) stop();
      setError((current) => clearFieldError(current, field));
    },
    turnstileCleared: () => {
      // 위젯이 풀렸으면 예약된 Turnstile 재안내만 취소한다 — 다른 오류 예약은 남긴다.
      if (scheduled?.retry === "turnstile") stop();
      setError((current) => (current?.retry === "turnstile" ? null : current));
    },
    dispose: stop,
  };
};
